import { ReactElement, useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useCAWContext } from '../CAW';
import {
	getDate,
	addDays,
	format,
	isAfter,
	isSameDay,
	startOfDay,
	startOfMonth,
	endOfDay,
	isBefore,
	addYears,
	formatDate,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Oj, Value } from '@fshr/shared/types';
import Calendar, { CalendarStatus, CalendarProps, TileArgs, isRestricted } from '../../calendar';
import { PlanStore } from '@micro-apps/stores';
import { translate } from '@micro-apps/env';
import { parseAvailability } from '@fshr/shared';
import { cn } from '@fshr/shared-react';
import { getMaxCheckoutDate, isValidCheckoutForStay } from './calendarAvailability';
import { useNightlyRatesCalendar } from '@fshr/experiments';

type Props = {
	setupScrollEvent?: boolean;
	openDialog?: () => unknown;
	onClose?: () => unknown;
	baseClassName?: string;
};
export default function DatePicker({
	openDialog,
	baseClassName,
	onClose,
}: Props): ReactElement<any> {
	const { planStore, timezone, isDesktop, locale } = useCAWContext();
	const { enabled: nightlyRatesEnabled } = useNightlyRatesCalendar();
	const dayPicker = useRef<HTMLDivElement>(null);
	const onChange = useCallback(
		(value: Value) => {
			if (Array.isArray(value)) {
				const [checkIn, checkOut] = value;
				if (checkIn && checkOut) {
					const checkinDate = planStore?.search.checkinDate
						? new Date(planStore.search.checkinDate)
						: null;
					if (checkinDate && isAfter(checkinDate, checkIn)) {
						planStore?.updateSearch({
							checkinDate: new Date(checkIn),
							checkoutDate: undefined,
						});
					} else {
						planStore?.updateSearch({
							checkinDate: new Date(checkIn),
							checkoutDate: isSameDay(checkIn, checkOut)
								? addDays(checkOut, 1)
								: new Date(checkOut),
						});
					}
				} else if (checkIn) {
					planStore?.updateSearch({
						checkinDate: new Date(checkIn),
						checkoutDate: undefined,
					});
				} else if (checkOut) {
					planStore?.updateSearch({
						checkinDate: planStore?.search.checkinDate,
						checkoutDate:
							planStore?.search.checkinDate && isSameDay(planStore?.search.checkinDate, checkOut)
								? addDays(checkOut, 1)
								: new Date(checkOut),
					});
				}
			}
			setTimeout(() => {
				openDialog?.();
			});
		},
		[planStore, openDialog]
	);

	return (
		<div
			role="application"
			aria-label={`${translate('Calendar')} ${translate('Stay Dates')}`}
			className={cn('max-w-[590px]', {
				'pb-[70px]': !isDesktop && !nightlyRatesEnabled,
				'pb-[90px]': !isDesktop && nightlyRatesEnabled,
			})}
			ref={dayPicker}
		>
			<DateRangeCalendar
				onChange={onChange}
				onClose={onClose}
				isDesktop={isDesktop}
				locale={locale}
				baseClassName={baseClassName}
				isMobile={!isDesktop}
			/>
		</div>
	);
}

type DateRangeCalendarProps = {
	onChange: Exclude<CalendarProps['onChange'], undefined>;
	onClose?: () => void;
	isDesktop?: boolean;
	locale?: string;
	baseClassName?: string;
	isMobile?: boolean;
};

const DateRangeCalendar = observer(function DateRangeCalendar({
	onChange,
	isDesktop,
	locale = 'en',
	baseClassName = 'FSHCalendar',
	isMobile = false,
}: DateRangeCalendarProps) {
	const { planStore, timezone, dateFnsLocale } = useCAWContext();
	const { enabled: nightlyRatesEnabled } = useNightlyRatesCalendar();
	const minStart =
		planStore?.search?.checkinDate &&
		addDays(planStore?.search?.checkinDate, planStore?.config.minStay || 0);

	const maxEnd =
		planStore?.search?.checkinDate &&
		addDays(planStore?.search?.checkinDate, planStore?.config.maxStay || 0);

	const availability = useMemo(
		() =>
			planStore?.calendarResult?.calendar?.[0]?.availability
				? parseAvailability(planStore?.calendarResult?.calendar?.[0]?.availability)
				: {
						restricted: [],
						unavailable: [],
						notBookable: [],
					},
		[planStore?.calendarResult?.calendar]
	);

	const tileDisabled = useCallback(
		(t: TileArgs) => {
			const day = new Date(t.date);
			const status = getCalendarStatus(day, planStore);
			const checkIn = planStore?.search.checkinDate;
			const selectingCheckout = !!(checkIn && !planStore?.search.checkoutDate);

			//days before check-in restart the range as a new check-in (see Calendar onChange), so they
			//fall through to the check-in rules instead of being judged as checkout targets
			if (selectingCheckout && checkIn && !isBefore(day, checkIn)) {
				//Selected check-in is not a valid checkout target; keep it enabled for selection styling
				if (isSameDay(day, checkIn)) {
					return false;
				}

				//min-stay gap between check-in and earliest checkout
				if (minStart && isAfter(day, checkIn) && isBefore(day, minStart)) {
					return true;
				}
				const maxCheckout = getMaxCheckoutDate(checkIn, planStore, maxEnd);
				//days after maxStay / lastCheckout are not selectable
				if (maxCheckout && isAfter(day, maxCheckout)) {
					return true;
				}
				//stay nights [checkIn, day) must be allowed; the checkout day itself is not checked
				return !isValidCheckoutForStay(checkIn, day, planStore);
			}

			//not bookable / outside the usable window. Sold-out (0) is a valid check-in.
			return !isSameDay(new Date(), day) && status === '.';
		},
		[planStore, minStart, maxEnd]
	);
	const tileClassName = useCallback(
		(t: TileArgs) => {
			const day = new Date(t.date);
			const result: string[] = [`${baseClassName}Day_button_${format(day, 'yyyy_MM_dd')}`];
			const { blocked } = dayMetadata(day, maxEnd, minStart, planStore);

			if (blocked !== '') {
				result.push(`${baseClassName}Day__blocked_out_of_range`);
			}
			if (isRestricted(day, availability.restricted)) {
				result.push(`${baseClassName}Day__highlighted_calendar`);
			}
			// Use raw status for '0' only — '.' out-of-range days stay without strikethrough class
			if (getCalendarStatus(day, planStore) === '0') {
				result.push(`${baseClassName}Day__blocked_calendar`);
			}
			return result;
		},
		[maxEnd, minStart, planStore, availability, baseClassName]
	);
	const tileAriaLabel = useCallback(
		(d: Date) => {
			const day = new Date(d);
			const formattedDate = `${format(day, 'EEEE')}, ${format(day, 'MMMM d, yyyy')}`;
			const originalStatus = getCalendarStatus(day, planStore);
			const { calendarStatus, blocked, isDayAfterCheckin, isLastCheckoutDate } = dayMetadata(
				day,
				maxEnd,
				minStart,
				planStore
			);

			if (originalStatus === 'R') {
				return `${translate('Restricted.')} ${formattedDate}`;
			}
			if (originalStatus === '0') {
				return `${translate('Unavailable.')} ${formattedDate}`;
			}
			if (isDayAfterCheckin) {
				return `${translate('Available for checkout')} ${formattedDate}`;
			}
			if (blocked || isLastCheckoutDate) {
				return `${
					blocked === 'M' ? translate('Minimum nights required.') : translate('Out of Range')
				} ${formattedDate}`;
			}
			if (calendarStatus === '9') {
				return planStore?.search.checkinDate && planStore?.search.checkoutDate
					? `${translate('Available check-in date')} ${formattedDate}`
					: `${translate('Available check-out date')} ${formattedDate}`;
			}
			return `${translate('Available')} ${formattedDate}`;
		},
		[maxEnd, minStart, planStore]
	);
	const navigationAriaLabel = useCallback(() => {
		return planStore?.search?.checkinDate && planStore?.search?.checkoutDate
			? `${translate('Selected Dates')} ${format(
					planStore?.search?.checkinDate,
					'MMMM d yyyy'
				)} to ${format(planStore?.search?.checkoutDate, 'MMMM d yyyy')}${
					planStore?.search.flexDatesSelected
						? ` | ± ${planStore?.search.flexDatesSelected} ${translate('days')}`
						: ''
				}`
			: planStore?.search?.checkinDate
				? `${translate('Selected Check-in Date')} ${format(
						planStore?.search?.checkinDate,
						'MMMM d yyyy'
					)}`
				: '';
	}, [planStore]);
	const value: [Date, Date] | undefined =
		planStore?.search.checkinDate && planStore.search.checkoutDate
			? [planStore?.search.checkinDate, planStore.search.checkoutDate]
			: undefined;
	const nightlyRates = getCalendarRates(planStore);

	const checkin = planStore?.search?.checkinDate;
	const checkout = planStore?.search?.checkoutDate;
	const formattedDateRange =
		checkin && checkout
			? `${formatDate(checkin, 'P', { locale: dateFnsLocale?.localeMappings[locale || 'en'] })} - ${formatDate(checkout, 'P', { locale: dateFnsLocale?.localeMappings[locale || 'en'] })} ${
					planStore?.search.flexDatesSelected
						? ` | ±${planStore?.search.flexDatesSelected} ${translate('days')}`
						: ''
				}`
			: undefined;

	const lastUpdated = useMemo(
		() => planStore?.calendarResult?.lastUpdated,
		[planStore?.calendarResult]
	);

	return (
		<Calendar
			onChange={onChange}
			showDoubleView={isDesktop}
			nextAriaLabel={translate('Next month')}
			prevAriaLabel={translate('Previous month')}
			navigationAriaLabel={navigationAriaLabel}
			navigationAriaLive="polite"
			selectRange={true}
			strictRangeSelect={true}
			next2Label={null}
			prev2Label={null}
			value={value}
			goToRangeStartOnSelect={false}
			tileClassName={tileClassName}
			tileAriaLabel={tileAriaLabel}
			tileDisabled={tileDisabled}
			locale={locale}
			formatShortWeekday={(_, d) => format(d, locale === 'en' ? 'eeeee' : 'eeeeee')}
			minDate={startOfDay(toZonedTime(new Date(), timezone!))}
			maxDate={endOfDay(addYears(toZonedTime(new Date(), timezone!), 1))}
			allowPartialRange={true}
			timezone={timezone}
			restrictedDates={availability?.restricted}
			unavailableDates={availability?.unavailable}
			allowUnavailableSelection
			baseClassName={`${baseClassName}View`}
			showLegend={isMobile}
			showFooter={false}
			isMobile={isMobile}
			showNavigation={!isMobile}
			showMobileHeader={false}
			valueLabel={formattedDateRange}
			lastUpdated={lastUpdated}
			availabilityData={nightlyRatesEnabled ? nightlyRates : undefined}
			nightlyRatesExperiment={nightlyRatesEnabled}
		/>
	);
});

type OutsideRangeOpts = {
	day: Date;
	maxEnd?: Date;
	minStart?: Date;
	planStore?: PlanStore;
};

function isOutsideRange({ day, maxEnd, minStart, planStore }: OutsideRangeOpts): 'O' | 'M' | '' {
	let result: 'O' | 'M' | '' = '';
	result =
		getCalendarStatus(day, planStore) === '.' ||
		(planStore?.config.firstBookableDate && isBefore(day, planStore?.config.firstBookableDate))
			? 'O'
			: '';

	if (!result) {
		if (planStore?.search.checkinDate && !planStore?.search.checkoutDate) {
			if (planStore?.config.minStay && planStore?.config.minStay > 1) {
				result =
					minStart && isBefore(day, minStart) && isAfter(day, planStore?.search.checkinDate)
						? 'M'
						: '';
			}
			const maxCheckout = getMaxCheckoutDate(planStore.search.checkinDate, planStore, maxEnd);
			result = result || (maxCheckout && isAfter(day, maxCheckout) ? 'O' : '');
		}
		result =
			result ||
			(planStore?.config.lastCheckoutDate && isAfter(day, planStore?.config.lastCheckoutDate))
				? 'O'
				: '';
	}
	return result;
}

function getCalendarStatus(day: Date, planStore?: PlanStore): CalendarStatus {
	const firstOfMonth = format(startOfMonth(day), 'yyyy-MM-dd');
	const calendarResult = planStore?.calendarResult;
	const availability = calendarResult?.calendar?.[0]?.availability?.find(
		a => a.date === firstOfMonth
	);
	let status: '0' | '9' | '.' | 'R' | '?' = '?';
	if (availability && availability.status) {
		status = availability.status.split('')[getDate(day) - 1] as CalendarStatus;
	}

	return status;
}

function getCalendarRates(planStore?: PlanStore): Oj.CalendarAvailability[] {
	if (!planStore?.calendarResult?.calendar?.[0]?.availability) return [];

	return planStore.calendarResult.calendar[0].availability;
}

function dayMetadata(day: Date, maxEnd?: Date, minStart?: Date, planStore?: PlanStore) {
	const originalCalendarStatus = getCalendarStatus(day, planStore);
	let calendarStatus = originalCalendarStatus;
	let blocked = isOutsideRange({
		day,
		maxEnd,
		minStart,
		planStore,
	});
	// allow min stay after checkin as checkout regardless
	const isDayAfterCheckin =
		!planStore?.search.checkoutDate &&
		planStore?.search.checkinDate &&
		isSameDay(addDays(planStore?.search.checkinDate, planStore?.config.minStay || 0), day);

	// don't allow last checkout as checkin
	const isLastCheckoutDate =
		(!planStore?.search?.checkinDate ||
			(planStore?.search.checkinDate && planStore?.search.checkoutDate)) &&
		planStore?.config?.lastCheckoutDate &&
		isSameDay(planStore?.config?.lastCheckoutDate, day);

	if (isDayAfterCheckin) {
		blocked = '';
		//preserve restricted/unavailable visual + semantic status
		if (originalCalendarStatus !== 'R' && originalCalendarStatus !== '0') {
			calendarStatus = '.';
		}
	} else if (blocked || isLastCheckoutDate) {
		blocked = blocked || 'O';
	}

	return {
		calendarStatus,
		blocked,
		isDayAfterCheckin,
		isLastCheckoutDate,
	};
}
